import { Minus, Plus } from 'lucide-react';
import type { ReactElement } from 'react';
import { parseOrderQty, stepOrderQty } from '@/entities/product';
import { formatCount } from '@/shared/lib';
import { Input, Label } from '@/shared/ui';

interface QuantityStepperProps {
  /** 입력창 문자열 그대로 — 직접 입력과 −/+ 가 같은 상태를 고친다. */
  readonly value: string;
  readonly minOrderQty: number;
  readonly onChange: (value: string) => void;
}

const QTY_INPUT_ID = 'store-order-qty';

/** 원형 −/+ 버튼 — 작은 조작부만 알약(원형) 모양으로 둔다. */
const STEP_BUTTON_CLASS =
  'inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-foreground/15 bg-background transition-colors hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-foreground/15';

/**
 * 수량 선택 — 연회색 칸 안의 −/+ 와 숫자 입력.
 * −/+ 는 최소 주문 수량 아래로 내려가지 않고, 직접 입력은 그대로 받아 아래 예상 합계가 검증 결과를 보여 준다.
 */
export const QuantityStepper = ({
  value,
  minOrderQty,
  onChange,
}: QuantityStepperProps): ReactElement => {
  const qty = parseOrderQty(value);

  return (
    <div className="flex items-center justify-between gap-4 bg-muted/70 px-5 py-4">
      <div className="space-y-0.5">
        <Label htmlFor={QTY_INPUT_ID} className="text-sm font-semibold">
          수량
        </Label>
        <p className="text-xs text-muted-foreground">
          최소 {formatCount(minOrderQty)}개부터 주문할 수 있습니다
        </p>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="수량 줄이기"
          // 이미 최소 주문 수량이면 더 줄일 수 없다. 입력이 비었거나 잘못됐으면 눌러서 최소 수량으로 되돌린다.
          disabled={qty !== null && qty <= minOrderQty}
          onClick={() => onChange(String(stepOrderQty(qty, minOrderQty, -1)))}
          className={STEP_BUTTON_CLASS}
        >
          <Minus aria-hidden className="size-4" strokeWidth={1.5} />
        </button>
        <Input
          id={QTY_INPUT_ID}
          inputMode="numeric"
          autoComplete="off"
          className="h-9 w-24 bg-background text-center tabular-nums"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          aria-label="수량 늘리기"
          onClick={() => onChange(String(stepOrderQty(qty, minOrderQty, 1)))}
          className={STEP_BUTTON_CLASS}
        >
          <Plus aria-hidden className="size-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
};
